import React, { FC } from 'react';
import { AppBundle } from 'api/optimalprint-sdk.d';
import { cloneDeep } from 'lodash';
import ReactPlaceholder from 'react-placeholder';
import getLoadingProducts from 'store/publicAssetProduct/selector/getLoadingProducts';
import { useSelector } from 'react-redux';
import ProductAttributeSelector from '../ProductAttributeSelector/ProductAttributeSelector';
import styles from './StudioProductAttributes.module.scss';

export interface StudioProductAttributes {
  studioProductPageSelectors: any[];
  currentProductAttributes: AppBundle.Api.Entity.App.V1.AbstractProduct.ProductItem.IAttribute[];
  currentProductItem?: AppBundle.Api.Entity.App.V1.AbstractProduct.IProductItem;
  allProducts?: AppBundle.Api.Entity.App.V1.AbstractProduct.IProductItem[];
  changeAttribute: (typeName: string, value: string, defaultProductId: number) => void;
  productId: number;
}

const productMatchAttributes = (product: ProductItem, attributesToMatch: AbstractProductAttribute[]) =>
  !attributesToMatch.some((attribute) => {
    const attrMatch = (product.attributes || []).find(
      (productAttribute) =>
        productAttribute.typeName === attribute.typeName && productAttribute.value === attribute.value,
    );
    return !attrMatch;
  });

type AbstractProductAttribute = AppBundle.Api.Entity.App.V1.AbstractProduct.ProductItem.IAttribute;
type ProductItem = AppBundle.Api.Entity.App.V1.AbstractProduct.IProductItem;

const filterProducts = (products: ProductItem[], attributesToFilter: AbstractProductAttribute[]) => {
  if (attributesToFilter.length === 0) {
    return products;
  }
  return products.filter((productItem) => productMatchAttributes(productItem, attributesToFilter));
};

const getProductAttributeValue = (
  product: AppBundle.Api.Entity.App.V1.AbstractProduct.IProductItem,
  attributeName: string,
) => {
  if (product) {
    const attributeWithValue = product.attributes?.find((atr) => atr.typeName === attributeName);
    return attributeWithValue ? attributeWithValue.value : undefined;
  }
  return undefined;
};

const getSpecifiedAttributes = (attributesToFilter: string[], attributes: any[]) => {
  if (attributesToFilter.length === 0) {
    return [];
  }
  return attributes.filter((attribute) => attributesToFilter.indexOf(attribute.typeName) !== -1);
};

export type ExtendedProductPageSelectorOption = {
  disabled: boolean;
  selected: boolean;
  productId?: number;
  hexColor2?: string;
  value: string;
  name: string;
  order: any;
};

const getAvailableOptions = (selector: any, products: AppBundle.Api.Entity.App.V1.AbstractProduct.IProductItem[]) => {
  const options = cloneDeep(selector.options) as ExtendedProductPageSelectorOption[];
  options.forEach((option) => {
    option.disabled = true;
    option.selected = false;
  });

  products.forEach((productItem) => {
    const value = getProductAttributeValue(productItem, selector.typeName);
    const targetOption = options.find((option) => option.value === value);
    if (targetOption && !targetOption.productId) {
      targetOption.productId = productItem.productId;
      targetOption.disabled = false;
      targetOption.selected = false;
    }
  });
  return options;
};

const markSelectedOption = (options: ExtendedProductPageSelectorOption[], value: string, productId: number) => {
  const targetOption = options.find((option) => option.value === value);
  if (targetOption) {
    targetOption.selected = true;
    targetOption.productId = productId;
  }
  return options;
};

const StudioAttributesPlaceholder = () => {
  return (
    <>
      {Array.from({ length: 2 }, (v, i) => (
        <div key={`${i}`}>
          <ReactPlaceholder
            type="rect"
            ready={false}
            showLoadingAnimation
            style={{ width: '100%', height: '22px', marginBottom: '10px' }}
          >
            <div />
          </ReactPlaceholder>
          <ReactPlaceholder
            type="rect"
            ready={false}
            showLoadingAnimation
            style={{ width: '100%', height: '40px', marginBottom: '16px' }}
          >
            <div />
          </ReactPlaceholder>
        </div>
      ))}
    </>
  );
};

const StudioProductAttributes: FC<StudioProductAttributes> = ({
  studioProductPageSelectors,
  currentProductAttributes,
  currentProductItem,
  allProducts,
  changeAttribute,
  productId,
}) => {
  const fixedSelectors = studioProductPageSelectors
    .filter((selector) => selector.isHidden)
    .map((selector) => selector.typeName);
  const fixedAttributes = getSpecifiedAttributes(fixedSelectors, currentProductAttributes);
  const loadingProducts = useSelector(getLoadingProducts);

  if (loadingProducts) {
    return <StudioAttributesPlaceholder />;
  }

  let availableProducts = filterProducts(allProducts as any[], fixedAttributes);

  const renderSelector = (selector: any) => {
    availableProducts = filterProducts(availableProducts, fixedAttributes);
    const availableOptions = getAvailableOptions(selector, availableProducts);
    const currentProductAttributeValue =
      getProductAttributeValue(
        currentProductItem ||
          ({} as AppBundle.Api.Entity.Design.V1.DesignAbstractProduct.DesignProductItem.IProductItem),
        selector.typeName,
      ) || '';
    if (availableProducts.length === 0) {
      return null;
    }

    if (!currentProductAttributeValue) {
      return null;
    }
    const markedOptions = markSelectedOption(availableOptions, currentProductAttributeValue, productId);
    if (availableProducts.length > 0) {
      fixedAttributes.push({
        typeName: selector.typeName,
        value: currentProductAttributeValue || '',
      });
    }
    return (
      <ProductAttributeSelector
        key={`pa${selector.typeName}`}
        selector={selector}
        options={markedOptions}
        changeAttribute={changeAttribute}
      />
    );
  };

  return (
    <div className={styles.ProductAttributes}>
      {studioProductPageSelectors.map((selector) => renderSelector(selector))}
    </div>
  );
};

export default StudioProductAttributes;
